// On-device offline alert cache.
//
// Persists alert metadata + thumbnail JPEG bytes to IndexedDB so a user can
// review recent alerts from the login page even when the Parvekshak server is
// unreachable (see docs/offline-alerts-design.md).
//
// Access model: everything at rest is AES-GCM encrypted with a random device
// key — a non-extractable CryptoKey kept in this same IndexedDB (same scheme
// as localCredStore.js). No PIN: saved alerts must open instantly when the
// server is unreachable, and a PIN prompt there was one more thing standing
// between the user and the alert they needed. The key never exists in an
// exportable form, so a raw storage dump can't be decrypted — but anyone who
// can open this app on the device can see the saved alerts, and it does NOT
// protect against JS already running in this origin (XSS).
//
// Caches written by older builds were encrypted under a PIN-derived key that
// is no longer asked for, so they can't be read. They are wiped once and
// refill from the server on the next sync (see _loadKey).
//
// crypto.subtle only exists in a secure context (HTTPS / localhost), so this
// whole feature is gated on isOfflineCacheSupported(). Over plain http://<lan-ip>
// (Local Mode) it is unavailable — callers must check support first.

const DB_NAME  = 'prvsk_offline_alerts';
const DB_VER   = 1;
const S_META   = 'meta';
const S_ALERTS = 'alerts';
const S_THUMBS = 'thumbs';

const META_KEY      = 'config';        // single row holding the device key, cap, etc.

// Extract a chronological sort key (YYYYMMDD_HHMMSS) from an alert filename,
// so ordering/eviction work even when the API omits an explicit `ts`.
function tsFromId(id) {
  const m = String(id).match(/(\d{8})_(\d{6})/);
  return m ? `${m[1]}_${m[2]}` : '';
}
const DEFAULT_CAP   = 500;

// ─── capability ───────────────────────────────────────────────────────────────

export function isOfflineCacheSupported() {
  return (
    typeof indexedDB !== 'undefined' &&
    typeof crypto !== 'undefined' &&
    !!crypto.subtle &&
    typeof crypto.subtle.generateKey === 'function'
  );
}

// ─── IndexedDB plumbing ───────────────────────────────────────────────────────

let _dbPromise = null;
function openDb() {
  if (!_dbPromise) {
    _dbPromise = new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, DB_VER);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(S_META))   db.createObjectStore(S_META);
        if (!db.objectStoreNames.contains(S_ALERTS)) db.createObjectStore(S_ALERTS, { keyPath: 'id' });
        if (!db.objectStoreNames.contains(S_THUMBS)) db.createObjectStore(S_THUMBS, { keyPath: 'id' });
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror   = () => reject(req.error);
    });
  }
  return _dbPromise;
}

function tx(db, store, mode) {
  return db.transaction(store, mode).objectStore(store);
}
function reqP(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror   = () => reject(request.error);
  });
}
async function idbGet(store, key)        { return reqP(tx(await openDb(), store, 'readonly').get(key)); }
async function idbPut(store, value, key) { return reqP(tx(await openDb(), store, 'readwrite').put(value, key)); }
async function idbDel(store, key)        { return reqP(tx(await openDb(), store, 'readwrite').delete(key)); }
async function idbCount(store)           { return reqP(tx(await openDb(), store, 'readonly').count()); }
async function idbClear(store)           { return reqP(tx(await openDb(), store, 'readwrite').clear()); }

// Cursor walk (newest-first) — ids are alert filenames that sort chronologically.
async function idbAll(store) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const out = [];
    const req = tx(db, store, 'readonly').openCursor(null, 'prev');
    req.onsuccess = () => {
      const cur = req.result;
      if (cur) { out.push(cur.value); cur.continue(); }
      else resolve(out);
    };
    req.onerror = () => reject(req.error);
  });
}

// ─── crypto ───────────────────────────────────────────────────────────────────

const enc = new TextEncoder();
const dec = new TextDecoder();

// Spreading a whole buffer into String.fromCharCode(...bytes) blows the JS
// engine's call-stack/argument-count limit once a payload gets into the tens
// of KB (a thumbnail's ciphertext easily does) — and that limit is noticeably
// tighter on Android WebView's mobile V8 than on a desktop browser, so it
// fails there first. Chunk the conversion so there's no size ceiling.
function toB64(buf) {
  const bytes = new Uint8Array(buf);
  let binary = '';
  const CHUNK = 0x8000; // 32K bytes/call — well under any engine's arg limit
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}
const fromB64 = (b64) => Uint8Array.from(atob(b64), c => c.charCodeAt(0));

async function encrypt(key, bytes) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, bytes);
  return { iv: toB64(iv), ct: toB64(ct) };
}
async function decrypt(key, { iv, ct }) {
  const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: fromB64(iv) }, key, fromB64(ct));
  return new Uint8Array(pt);
}
const encJson = (key, obj)  => encrypt(key, enc.encode(JSON.stringify(obj)));
const decJson = async (key, blob) => JSON.parse(dec.decode(await decrypt(key, blob)));

// ─── lifecycle: device key ────────────────────────────────────────────────────

async function readMeta() { return (await idbGet(S_META, META_KEY)) || null; }

export async function isCacheInitialized() {
  if (!isOfflineCacheSupported()) return false;
  try { return !!(await readMeta()); } catch { return false; }
}

let _keyPromise = null;

// Resolves the device key, or null if the cache was never set up and `create`
// is false (reads shouldn't conjure an empty cache into existence). A legacy
// PIN-era cache (meta without a key) is unreadable now — wipe its data and
// start over under a fresh device key, keeping the user's cap.
async function _loadKey(create) {
  const meta = await readMeta();
  if (meta?.key) return meta.key;
  if (!meta && !create) return null;
  if (meta) { await idbClear(S_ALERTS); await idbClear(S_THUMBS); }
  const key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
  await idbPut(S_META, {
    key,
    cap: meta?.cap ?? DEFAULT_CAP,
    schema: DB_VER,
    lastSyncedAt: 0,
  }, META_KEY);
  return key;
}

function getKey(create = false) {
  if (!isOfflineCacheSupported()) return Promise.resolve(null);
  if (!_keyPromise) {
    _keyPromise = _loadKey(create).catch(() => null).then(k => {
      if (!k) _keyPromise = null; // not set up yet — let a later write create it
      return k;
    });
  }
  return _keyPromise;
}

// Turning sync on: make sure the cache and its key exist.
export async function init() {
  if (!isOfflineCacheSupported()) throw new Error('Offline cache unavailable in this context');
  _keyPromise = null;
  if (!(await getKey(true))) throw new Error('Could not set up on-device storage');
}

// ─── writes (best-effort; create the cache on first use) ──────────────────────────────

// Upsert alert metadata records. `alerts` = array of {id/filename, ts, ...}.
// Never throws — caching must not disturb the live experience.
export async function putAlerts(alerts) {
  if (!Array.isArray(alerts) || !alerts.length) return;
  try {
    const _key = await getKey(true);
    if (!_key) return;
    for (const a of alerts) {
      const id = a.id || a.filename;
      if (!id) continue;
      const existing = await idbGet(S_ALERTS, id);
      const prev = existing ? await decJson(_key, existing.data).catch(() => ({})) : {};
      const rec = {
        id,
        ts:       a.ts || prev.ts || tsFromId(id),
        camera:   a.camera ?? prev.camera ?? null,
        type:     a.type ?? prev.type ?? (id.split('_')[0] || 'unknown'),
        verdict:  a.verdict ?? prev.verdict ?? null,
        has_face: a.has_face ?? prev.has_face ?? false,
        ev:       prev.ev ?? null,
        cachedAt: Date.now(),
      };
      await idbPut(S_ALERTS, { id, ts: rec.ts, data: await encJson(_key, rec) });
    }
    await enforceQuota();
  } catch { /* best-effort */ }
}

// Enrich a record after the detail view fetched /alerts/{id}/info.
export async function putAlertDetail(id, ev, verdict) {
  if (!id) return;
  try {
    const _key = await getKey(true);
    if (!_key) return;
    const existing = await idbGet(S_ALERTS, id);
    const prev = existing ? await decJson(_key, existing.data) : { id, ts: tsFromId(id) };
    const rec = { ...prev, ev: ev ?? prev.ev ?? null, verdict: verdict ?? prev.verdict ?? null };
    await idbPut(S_ALERTS, { id, ts: rec.ts || tsFromId(id), data: await encJson(_key, rec) });
  } catch { /* best-effort */ }
}

// Store thumbnail bytes (skips if already present). `blob` is a Blob/ArrayBuffer.
export async function putThumb(id, blob) {
  if (!id) return;
  try {
    const _key = await getKey(true);
    if (!_key) return;
    if (await idbGet(S_THUMBS, id)) return; // already cached
    const buf   = blob instanceof Blob ? await blob.arrayBuffer() : blob;
    const bytes = buf.byteLength ?? new Uint8Array(buf).length;
    const mime  = blob?.type || 'image/jpeg';
    await idbPut(S_THUMBS, { id, mime, bytes, data: await encrypt(_key, new Uint8Array(buf)) });
  } catch (e) {
    // Best-effort — caching must never disturb the live view — but log it:
    // a silent catch here is exactly what hid the toB64 call-stack bug, so
    // don't repeat that mistake for whatever the next failure mode is.
    // eslint-disable-next-line no-console
    console.warn('[offlineAlertStore] putThumb failed for', id, e);
  }
}

export async function hasThumb(id) {
  try { return !!(await idbGet(S_THUMBS, id)); } catch { return false; }
}

export async function setLastSyncedAt(ts) {
  try {
    const meta = await readMeta();
    if (meta) await idbPut(S_META, { ...meta, lastSyncedAt: ts }, META_KEY);
  } catch { /* ignore */ }
}

// ─── reads (offline path) ─────────────────────────────────────

export async function listCachedAlerts({ page = 0, perPage = 20, type = 'all' } = {}) {
  const _key = await getKey();
  if (!_key) return { alerts: [], total: 0 };
  const rows = await idbAll(S_ALERTS);
  const decoded = [];
  for (const r of rows) {
    try { decoded.push(await decJson(_key, r.data)); } catch { /* skip unreadable */ }
  }
  // Order newest-first by the embedded timestamp (YYYYMMDD_HHMMSS sorts
  // chronologically). Filenames themselves sort by type prefix, not time.
  decoded.sort((a, b) => String(b.ts).localeCompare(String(a.ts)));
  const filtered = type && type !== 'all' ? decoded.filter(a => a.type === type) : decoded;
  const total = filtered.length;
  const slice = filtered.slice(page * perPage, (page + 1) * perPage);
  return { alerts: slice, total };
}

export async function getCachedAlert(id) {
  try {
    const _key = await getKey();
    if (!_key) return null;
    const row = await idbGet(S_ALERTS, id);
    return row ? decJson(_key, row.data) : null;
  } catch { return null; }
}

// Decrypt a thumbnail into an object URL. Caller must revoke it on unmount.
export async function getThumbObjectUrl(id) {
  try {
    const _key = await getKey();
    if (!_key) return null;
    const row = await idbGet(S_THUMBS, id);
    if (!row) return null;
    const bytes = await decrypt(_key, row.data);
    return URL.createObjectURL(new Blob([bytes], { type: row.mime || 'image/jpeg' }));
  } catch { return null; }
}

// ─── maintenance ──────────────────────────────────────────────────────────────

export async function getCap() {
  try { return (await readMeta())?.cap ?? DEFAULT_CAP; } catch { return DEFAULT_CAP; }
}
export async function setCap(cap) {
  const meta = await readMeta();
  if (meta) { await idbPut(S_META, { ...meta, cap }, META_KEY); await enforceQuota(); }
}

// Evict the oldest alerts (by embedded timestamp) + their thumbs beyond the cap.
export async function enforceQuota() {
  try {
    const cap = await getCap();
    const rows = await idbAll(S_ALERTS); // rows carry a top-level `ts` sort key
    if (rows.length <= cap) return;
    // Oldest-first by ts, then delete everything past the cap.
    rows.sort((a, b) => String(a.ts).localeCompare(String(b.ts)));
    const victims = rows.slice(0, rows.length - cap);
    for (const r of victims) {
      await idbDel(S_ALERTS, r.id);
      await idbDel(S_THUMBS, r.id);
    }
  } catch { /* ignore */ }
}

export async function stats() {
  try {
    const meta   = await readMeta();
    const count  = await idbCount(S_ALERTS);
    const thumbs = await idbAll(S_THUMBS);
    const bytes  = thumbs.reduce((n, t) => n + (t.bytes || 0), 0);
    return { count, bytes, lastSyncedAt: meta?.lastSyncedAt || 0, cap: meta?.cap ?? DEFAULT_CAP };
  } catch { return { count: 0, bytes: 0, lastSyncedAt: 0, cap: DEFAULT_CAP }; }
}

// Wipe everything (data + device key). Mirrors localCredStore.clearAll().
export async function clearAll() {
  _keyPromise = null;
  try {
    await idbClear(S_ALERTS);
    await idbClear(S_THUMBS);
    await idbClear(S_META);
  } catch { /* ignore */ }
  _dbPromise = null;
  await new Promise((resolve) => {
    const req = indexedDB.deleteDatabase(DB_NAME);
    req.onsuccess = req.onerror = req.onblocked = () => resolve();
  });
}
